import InPageBanner from '../../../../components/InPageBanner';
import styled from 'styled-components';
import { AnalyticsLink } from '../../../../components/Button';
import { theme } from '../../../../styles/theme';
import { MaybeNil } from '../../../../utils/interfaces';
import { IMaxSchemasFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';

const Title = styled.span`
  color: ${theme.colors.secondary900};
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
`;
const Content = styled.p`
  color: ${theme.colors.secondary800};
  line-height: 20px;
  font-size: 14px;
`;

const Highlight = styled.span`
  font-weight: 600;
`;
const Link = styled(AnalyticsLink)`
  font-size: 14px;
  color: ${theme.colors.secondary900};
  text-decoration: underline;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface IProps {
  maxSchemas: MaybeNil<IMaxSchemasFieldsFragment>;
}

interface IBannerContentProps {
  availableSchemas: number;
  maxSchemas: number;
}

const BannerContent = ({
  availableSchemas,
  maxSchemas,
}: IBannerContentProps) => {
  if (availableSchemas < 0) {
    return dataModelLabelInfoTooltipOptions.maxSchemasExceeded.content(
      Math.abs(availableSchemas),
    );
  }

  return (
    <>
      Your team has reached the maximum amount of
      <Highlight>{` ${maxSchemas} data schemas `}</Highlight> allowed within
      your plan. Contact your representative to increase your schema limit.
    </>
  );
};

interface IBannerTitleProps {
  availableSchemas: number;
  maxSchemas: number;
}

const BannerTitle = ({ availableSchemas, maxSchemas }: IBannerTitleProps) => {
  const title =
    availableSchemas < 0
      ? `Max number of schemas (${maxSchemas}) exceeded`
      : `Max number of schemas (${maxSchemas}) reached`;

  return <Title>{title}</Title>;
};

const DataModelerMaxSchemaBanner = ({ maxSchemas }: IProps) => {
  if (!maxSchemas) {
    return null;
  }
  if (Number.isNaN(maxSchemas?.maxSchemas)) {
    return null;
  }

  if (
    maxSchemas &&
    !Number.isNaN(maxSchemas?.availableSchemas) &&
    maxSchemas.availableSchemas > 0
  ) {
    return null;
  }

  return (
    <InPageBanner type={'warning'}>
      <Container>
        <BannerTitle
          availableSchemas={maxSchemas.availableSchemas}
          maxSchemas={maxSchemas.maxSchemas}
        />
        <Content>
          <BannerContent
            availableSchemas={maxSchemas.availableSchemas}
            maxSchemas={maxSchemas.maxSchemas}
          />
        </Content>
        <Link to={'mailto:Support@kubit.ai'} target="_blank">
          Contact Support
        </Link>
      </Container>
    </InPageBanner>
  );
};

export default DataModelerMaxSchemaBanner;
